// #40 Rail: a status chain as 34 px pill steps joined by hairlines (the debrief chains, the nine panel-change
// statuses, the assessment chain, the Recruiter screen chain, the reference-check chain). A step is done (check on
// --t-green), on (blue ring, the current step), bad (red tint), warn (gold tint), skip (dashed: passed over) or next.
// `terminal` replaces the last step with a pill (*Blocked*, *Unable to Complete*): the chain ended on a branch. `wrap`
// lets the rail break onto two rows at ≤ 1180 px; otherwise it scrolls inside its card, and it scrolls its own box to the
// current step when drawn, when it starts to overflow and when the current step changes, so the current step is always
// in view (UAT F-96: at 1512 px the profile's stage track hid its current stage 88 px past the edge). A step with
// href/onOpen is focusable (F); `meta` is a right-aligned note.

import type { ReactNode } from "react";
import { Icon } from "../core/icons";
import { Pill } from "../core/Pill";
import type { Tone } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { PROGRESS_STATE_WORDS } from "./labels";
import { regionProps, useOverflow } from "../lib/useOverflow";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";

export type RailState = "done" | "on" | "bad" | "warn" | "skip" | "next";

export interface RailStep {
  /** The status word, from a labels module. */
  label: string;
  /** Default next. */
  state?: RailState | undefined;
  href?: string | undefined;
  onOpen?: (() => void) | undefined;
  hs?: Hs | undefined;
}

export interface RailProps {
  steps: readonly RailStep[];
  /** The chain's name. */
  label: string;
  /** The chain ended on a branch: this pill replaces the last step. */
  terminal?: { word: string; tone?: Tone | undefined } | undefined;
  meta?: ReactNode | undefined;
  wrap?: boolean | undefined;
  className?: string | undefined;
}

/** The state a sighted reader takes from the glyph or tint; `on` is aria-current, `warn` has no word yet. */
const STATE_WORD: Partial<Record<RailState, string>> = {
  done: PROGRESS_STATE_WORDS.completed, bad: PROGRESS_STATE_WORDS.blocked, skip: PROGRESS_STATE_WORDS.skipped,
};

/** The room kept beside the current step when the rail scrolls to it: the step's 3 px glow and a pixel of air. */
const STEP_AIR = 4;

/** The step a rail is at: the current (on) step, else the furthest step reached (a held stage is warn); -1 for none. */
export function currentStep(steps: readonly RailStep[]): number {
  const on = steps.findIndex((step) => step.state === "on");
  if (on >= 0) return on;
  return steps.reduce((at, step, index) => ((step.state ?? "next") === "next" ? at : index), -1);
}

/**
 * Scrolls the rail's own box sideways by the least that shows its current step whole (inline: nearest), never the page;
 * a step already in view stays where it is, and a step wider than the box shows its start.
 */
export function revealCurrent(region: Pick<HTMLElement, "scrollLeft" | "getBoundingClientRect" | "querySelectorAll">, index: number): void {
  const step = region.querySelectorAll<HTMLElement>(".mc-rs")[index];
  if (!step) return;
  const box = region.getBoundingClientRect();
  const at = step.getBoundingClientRect();
  if (at.left - STEP_AIR < box.left) region.scrollLeft -= box.left - at.left + STEP_AIR;
  else if (at.right + STEP_AIR > box.right) region.scrollLeft += Math.min(at.right - box.right + STEP_AIR, at.left - box.left - STEP_AIR);
}

function Step({ step, index }: { step: RailStep; index: number }) {
  const hot = useHs(step.hs);
  const state = step.state ?? "next";
  const mark = state === "done" ? <Icon name="check" size={14} /> : state === "bad" ? <Icon name="x" size={14} /> : index + 1;
  const body = (
    <>
      <span className="mc-rn" aria-hidden={typeof mark === "number" ? undefined : true}>{mark}</span>
      {step.label}
      {STATE_WORD[state] !== undefined && STATE_WORD[state] !== step.label ? <VisuallyHidden>{", "}{STATE_WORD[state]}</VisuallyHidden> : null}
    </>
  );
  const classes = cx("mc-rs", `mc-rs-${state}`, (step.href !== undefined || step.onOpen) && "mc-rs-link");
  const current = state === "on" ? ("step" as const) : undefined;
  if (step.href !== undefined) return <a className={classes} href={step.href} onClick={step.onOpen} aria-current={current} {...hot}>{body}</a>;
  if (step.onOpen) return <button type="button" className={classes} onClick={step.onOpen} aria-current={current} {...hot}>{body}</button>;
  return <span className={classes} aria-current={current}>{body}</span>;
}

export function Rail({ steps, label, terminal, meta, wrap, className }: RailProps) {
  const { ref, overflows } = useOverflow<HTMLDivElement>();
  const drawn = terminal ? steps.slice(0, Math.max(0, steps.length - 1)) : steps;
  const current = currentStep(drawn);
  useIsoLayoutEffect(() => {
    if (current >= 0 && ref.current) revealCurrent(ref.current, current);
  }, [current, overflows]);
  return (
    <div ref={ref} className={cx("mc-rail-w", wrap && "mc-rail-wrap", className)} {...regionProps(overflows, label)}>
      <ol className="mc-rail" aria-label={label}>
        {drawn.map((step, index) => (
          <li key={`${step.label}-${index}`} className="mc-rli">
            {index > 0 ? <span className="mc-rl" aria-hidden="true" /> : null}
            <Step step={step} index={index} />
          </li>
        ))}
        {terminal ? (
          <li className="mc-rli mc-rli-end">
            {drawn.length > 0 ? <span className="mc-rl" aria-hidden="true" /> : null}
            <Pill lg tone={terminal.tone}>{terminal.word}</Pill>
          </li>
        ) : null}
      </ol>
      {meta !== undefined && meta !== null ? <span className="mc-rail-meta">{meta}</span> : null}
    </div>
  );
}
